import { Button } from "@legal-500/legal500-design-system";
import type { CompanyReviewEvent, ReviewStatus } from "../../api/pendingCompanies.service";
import { formatDateTime } from "../../utils";
import { ReviewStatusBadge } from "./PendingCompanies/ReviewStatusBadge";
import styles from "./PendingCompanies/Review.module.css";

function decisionStatus(entry: CompanyReviewEvent): ReviewStatus | null {
  if (entry.action === "approve" || entry.action === "relink") return "approved";
  if (entry.action === "reject") return "rejected";
  return null;
}

function actionLabel(entry: CompanyReviewEvent) {
  if (entry.action === "create") {
    if (entry.source === "import") return "Created by CSV import";
    if (entry.source === "manual") return "Created manually";
    return "Company created";
  }
  if (entry.action === "approve" && entry.createdCompany === true)
    return "Approved and created company";
  if (entry.action === "approve" && entry.createdCompany === false)
    return "Approved and linked existing company";
  const labels: Record<string, string> = {
    approve: "Submission approved",
    reject: "Submission rejected",
    edit: "Name corrected",
    rename: "Company name updated",
    relink: "Linked company changed",
  };
  return labels[entry.action] ?? entry.action.replaceAll("_", " ");
}

function nameLabel(entry: CompanyReviewEvent) {
  if (entry.action === "edit") return "Name changed";
  if (entry.action === "approve" && entry.createdCompany === true) return "Approved as";
  if (entry.action === "approve" && entry.createdCompany === false) return "Linked to";
  if (entry.action === "relink") return "Linked to";
  if (entry.action === "rename") return "Renamed";
  return "Name";
}

function profileNote(entry: CompanyReviewEvent) {
  if (entry.action === "rename") {
    const count = entry.updatedProfileCount;
    if (count === null || count === undefined) return null;
    if (count === 0) return "No linked profiles updated.";
    return `${count} linked ${count === 1 ? "profile" : "profiles"} updated.`;
  }
  if (entry.profileLinked === null) return null;
  if (entry.action === "edit")
    return entry.profileLinked ? "User profile updated." : "User profile not changed.";
  return entry.profileLinked ? "User profile linked." : "User profile not linked.";
}

function NameValue({ entry }: { entry: CompanyReviewEvent }) {
  const single = entry.action === "reject" || entry.action === "create";
  const { previousName, newName } = entry;
  if (single || previousName === null || newName === null || previousName === newName)
    return <>{newName ?? previousName}</>;
  return (
    <>
      {previousName}
      <span aria-hidden="true"> → </span>
      <span className="sr-only"> to </span>
      {newName}
    </>
  );
}

export function CompanyHistory({
  events,
  page,
  total,
  disabled = false,
  onPageChange,
  emptyMessage = "No review actions yet.",
}: {
  events: CompanyReviewEvent[];
  page: number;
  total: number;
  disabled?: boolean;
  onPageChange: (page: number) => void;
  emptyMessage?: string;
}) {
  return (
    <>
      {events.length === 0 && <p>{emptyMessage}</p>}
      <ol className={styles.history}>
        {events.map(entry => {
          const decision = decisionStatus(entry);
          const note = profileNote(entry);
          return (
            <li key={entry.id} className={styles.event}>
              <div className={styles.eventHeading}>
                <h3 className={styles.eventTitle}>{actionLabel(entry)}</h3>
                {decision && <ReviewStatusBadge status={decision} />}
              </div>
              <p className={styles.eventMeta}>
                <time dateTime={entry.createdAt}>{formatDateTime(entry.createdAt)}</time>
                <span aria-hidden="true">·</span>
                <span>
                  by{" "}
                  <span className={entry.actorEmail ? styles.actor : styles.actorId}>
                    {entry.actorEmail ?? entry.actorId}
                  </span>
                </span>
              </p>
              {(entry.previousName !== null || entry.newName !== null) && (
                <dl className={styles.names}>
                  <dt>{nameLabel(entry)}</dt>
                  <dd className={styles.recordedName}>
                    <NameValue entry={entry} />
                  </dd>
                </dl>
              )}
              {entry.reason && (
                <figure className={styles.reason}>
                  <figcaption>Staff reason</figcaption>
                  <blockquote>{entry.reason}</blockquote>
                </figure>
              )}
              {note && <p className={styles.eventNote}>{note}</p>}
              <dl className={styles.identifiers}>
                {entry.actorEmail && (
                  <>
                    <dt>Staff ID</dt>
                    <dd>{entry.actorId}</dd>
                  </>
                )}
                {entry.previousCompanyId && (
                  <>
                    <dt>Previous company ID</dt>
                    <dd>{entry.previousCompanyId}</dd>
                  </>
                )}
                {entry.companyId && (
                  <>
                    <dt>Company ID</dt>
                    <dd>{entry.companyId}</dd>
                  </>
                )}
                {entry.submissionId && (
                  <>
                    <dt>Pending company ID</dt>
                    <dd>{entry.submissionId}</dd>
                  </>
                )}
              </dl>
            </li>
          );
        })}
      </ol>
      <div className={styles.pagination}>
        <Button
          color="primary"
          className={styles.brandButton}
          variant="outlined"
          size="small"
          disabled={page <= 1 || disabled}
          onClick={() => onPageChange(page - 1)}
        >
          Previous history
        </Button>
        <span>
          History page {page} of {Math.max(1, Math.ceil(total / 10))}
        </span>
        <Button
          color="primary"
          className={styles.brandButton}
          variant="outlined"
          size="small"
          disabled={page * 10 >= total || disabled}
          onClick={() => onPageChange(page + 1)}
        >
          Next history
        </Button>
      </div>
    </>
  );
}
